import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getMySurplus, deleteSurplus } from '@/lib/api';
import { getFirstImage } from '@/utils/imageUtils';
import { toast } from 'sonner';
import { 
  Plus, Zap, MapPin, Eye, Edit, Trash2, Package,
  MoreVertical, Clock, FileSpreadsheet, Download, Upload, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const MySurplus = () => {
  const [surplus, setSurplus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const { isUSA } = useLocale();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);

  useEffect(() => {
    fetchSurplus();
  }, []);

  const fetchSurplus = async () => {
    try {
      const data = await getMySurplus();
      setSurplus(data);
    } catch (error) {
      console.error('Error fetching surplus:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/surplus/template/excel`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'plantilla_saldos_texhub.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success(isUSA ? 'Template downloaded' : 'Plantilla descargada');
      }
    } catch (error) {
      toast.error(isUSA ? 'Error downloading template' : 'Error al descargar plantilla');
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/surplus/import/excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        toast.success(`${result.imported} ${isUSA ? 'surplus imported' : 'saldos importados'}`);
        if (result.errors && result.errors.length > 0) {
          toast.warning(`${result.errors.length} ${isUSA ? 'rows with errors' : 'filas con errores'}`);
        }
        fetchSurplus(); // Refresh list
      } else {
        toast.error(result.detail || (isUSA ? 'Import error' : 'Error al importar'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error importing file' : 'Error al importar archivo');
    } finally {
      setUploadingExcel(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImportImagesZip = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/surplus/import/images-zip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const result = await response.json();
      
      if (response.ok) {
        toast.success(`${result.images_added} ${isUSA ? 'images added' : 'imágenes agregadas'}`);
        if (result.errors && result.errors.length > 0) {
          toast.warning(`${result.errors.length} ${isUSA ? 'files with errors' : 'archivos con errores'}`);
        }
        fetchSurplus(); // Refresh list
      } else {
        toast.error(result.detail || (isUSA ? 'Import error' : 'Error al importar'));
      }
    } catch (error) {
      toast.error(isUSA ? 'Error importing images' : 'Error al importar imágenes');
    } finally {
      setUploadingExcel(false);
      if (zipInputRef.current) zipInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSurplus(deleteId);
      toast.success(isUSA ? 'Surplus deleted' : 'Excedente eliminado');
      setSurplus(surplus.filter(s => s.surplus_id !== deleteId));
    } catch (error) {
      toast.error(isUSA ? 'Error deleting' : 'Error al eliminar');
    } finally {
      setDeleteId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      disponible: { bg: '#34B772', text: isUSA ? 'Available' : 'Disponible' },
      reservado: { bg: '#F59E0B', text: isUSA ? 'Reserved' : 'Reservado' },
      vendido: { bg: '#6B7280', text: isUSA ? 'Sold' : 'Vendido' },
      expirado: { bg: '#EF4444', text: isUSA ? 'Expired' : 'Expirado' }
    };
    const style = styles[status] || styles.disponible;
    return (
      <Badge style={{ backgroundColor: style.bg }} className="text-white text-xs">
        {style.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: 'short' 
    });
  };

  return (
    <div data-testid="my-surplus-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-6 h-6 text-yellow-500" />
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {isUSA ? 'My Surplus' : 'Mis Excedentes'}
            </h1>
          </div>
          <p className="text-sm text-slate-500">
            {isUSA ? 'Manage your available inventory' : 'Administra tu inventario disponible'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Excel Buttons */}
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleDownloadTemplate}
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            {isUSA ? 'Template' : 'Plantilla'}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="text-amber-600 border-amber-200 hover:bg-amber-50"
            data-testid="import-excel-btn"
          >
            {uploadingExcel ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {isUSA ? 'Import Excel' : 'Importar Excel'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
            className="hidden"
          />
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => zipInputRef.current?.click()}
            disabled={uploadingExcel}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
            data-testid="import-zip-btn"
          >
            {uploadingExcel ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Upload className="w-4 h-4 mr-2" />
            )}
            {isUSA ? 'ZIP Images' : 'ZIP Imágenes'}
          </Button>
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            onChange={handleImportImagesZip}
            className="hidden"
          />
          
          <Link to="/surplus">
            <Button variant="outline" size="sm">
              {isUSA ? 'View marketplace' : 'Ver marketplace'}
            </Button>
          </Link>
          <Link to="/dashboard/surplus/new">
            <Button 
              size="sm" 
              className="hover:opacity-90"
              style={{ backgroundColor: '#34B772' }}
              data-testid="new-surplus-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isUSA ? 'Publish' : 'Publicar'}
            </Button>
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : surplus.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="font-semibold text-slate-700 mb-2">
              {isUSA ? "You don't have any published surplus" : 'No tienes excedentes publicados'}
            </h3>
            <p className="text-slate-500 text-sm mb-4">
              {isUSA ? 'Publish your available inventory and sell faster' : 'Publica tu inventario disponible y vende más rápido'}
            </p>
            <Link to="/dashboard/surplus/new">
              <Button style={{ backgroundColor: '#34B772' }} className="hover:opacity-90">
                <Plus className="w-4 h-4 mr-2" />
                {isUSA ? 'Publish surplus' : 'Publicar excedente'}
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {surplus.map((item) => (
            <Card key={item.surplus_id} className="overflow-hidden" data-testid={`surplus-item-${item.surplus_id}`}>
              <CardContent className="p-0">
                <div className="flex">
                  {/* Image */}
                  <div className="w-24 h-24 sm:w-32 sm:h-32 bg-slate-100 flex-shrink-0">
                    {item.images && item.images.length > 0 ? (
                      <img 
                        src={getFirstImage(item.images)} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-8 h-8 text-slate-300" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusBadge(item.status)}
                          <Badge variant="outline" className="text-xs">
                            {item.surplus_type_label}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-900 truncate">{item.name}</h3>
                        <p className="text-sm text-slate-500 line-clamp-1">{item.description}</p>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="flex-shrink-0">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/surplus/${item.surplus_id}`} className="flex items-center">
                              <Eye className="w-4 h-4 mr-2" />
                              {isUSA ? 'View listing' : 'Ver publicación'}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/dashboard/surplus/${item.surplus_id}/edit`} className="flex items-center">
                              <Edit className="w-4 h-4 mr-2" />
                              {isUSA ? 'Edit' : 'Editar'}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeleteId(item.surplus_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {isUSA ? 'Delete' : 'Eliminar'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {item.location}
                        </span>
                        <span>{item.quantity} {item.unit}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        {item.price ? (
                          <span className="font-bold text-[#1E4C8E]">
                            ${item.price.toLocaleString(isUSA ? 'en-US' : 'es-MX')}
                          </span>
                        ) : (
                          <span className="text-slate-500">{isUSA ? 'Quote' : 'A cotizar'}</span>
                        )}
                        <span className="text-slate-400 flex items-center gap-1 text-xs">
                          <Eye className="w-3 h-3" />
                          {item.views_count || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{isUSA ? 'Delete surplus?' : '¿Eliminar excedente?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {isUSA 
                ? 'This action cannot be undone. The surplus will be permanently deleted.'
                : 'Esta acción no se puede deshacer. El excedente será eliminado permanentemente.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isUSA ? 'Cancel' : 'Cancelar'}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {isUSA ? 'Delete' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MySurplus;
